import LinkExtension from "@tiptap/extension-link";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { ComponentProps, useCallback, useState } from "react";
import { FiBold, FiItalic } from "react-icons/fi";
import { IoMdRedo, IoMdUndo } from "react-icons/io";
import {
  RiDoubleQuotesL,
  RiLinkM,
  RiLinkUnlinkM,
  RiListOrdered,
  RiListUnordered,
  RiParagraph,
  RiYoutubeLine,
} from "react-icons/ri";
import { youtubeUrltoEmbed } from "~/utils/youtubeUrltoEmbed";
import Iframe from "./tiptap/iframe";

interface MenuButtonOwnProps {
  isActive?: boolean;
}

const MenuButton = ({
  isActive,
  children,
  className,
  ...props
}: MenuButtonOwnProps & ComponentProps<"button">) => {
  const baseClassName =
    "border-2 border-transparent hover:border-gray-800 rounded-md p-1";

  return (
    <button
      type="button"
      className={`${baseClassName} ${
        isActive ? "bg-gray-800 text-white" : "bg-white text-gray-800"
      } ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

const MenuBar = ({ editor }: { editor: Editor | null }) => {
  const setLink = useCallback(() => {
    if (!editor) {
      return null;
    }

    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();

      return;
    }

    // update link
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <>
      <MenuButton
        className="font-bold"
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive("bold")}
      >
        <FiBold />
      </MenuButton>
      <MenuButton
        className="italic"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive("italic")}
      >
        <FiItalic />
      </MenuButton>

      <MenuButton
        onClick={() => editor.chain().focus().setParagraph().run()}
        isActive={editor.isActive("paragraph")}
      >
        <RiParagraph />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        isActive={editor.isActive("heading", { level: 1 })}
      >
        h1
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor.isActive("heading", { level: 2 })}
      >
        h2
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        isActive={editor.isActive("heading", { level: 3 })}
      >
        h3
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
        isActive={editor.isActive("heading", { level: 4 })}
      >
        h4
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
        isActive={editor.isActive("heading", { level: 5 })}
      >
        h5
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
        isActive={editor.isActive("heading", { level: 6 })}
      >
        h6
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive("bulletList")}
      >
        <RiListUnordered />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive("orderedList")}
      >
        <RiListOrdered />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive("blockquote")}
      >
        <RiDoubleQuotesL />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        —
      </MenuButton>

      <MenuButton onClick={setLink} isActive={editor.isActive("link")}>
        <RiLinkM />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={!editor.isActive("link")}
      >
        <RiLinkUnlinkM />
      </MenuButton>

      <MenuButton
        onClick={() => {
          let url = window.prompt("Youtube URL");

          if (url?.includes("youtube")) {
            if (!url.includes("embed")) {
              url = youtubeUrltoEmbed(url);
            }

            editor.chain().focus().setIframe({ src: url }).run();
          }
        }}
        isActive={editor.isActive("iframe")}
      >
        <RiYoutubeLine />
      </MenuButton>
      <br />
      <MenuButton onClick={() => editor.chain().focus().unsetAllMarks().run()}>
        Clear marks
      </MenuButton>
      <MenuButton onClick={() => editor.chain().focus().clearNodes().run()}>
        Clear nodes
      </MenuButton>
      <MenuButton onClick={() => editor.chain().focus().setHardBreak().run()}>
        Hard break
      </MenuButton>
      <MenuButton onClick={() => editor.chain().focus().undo().run()}>
        <IoMdUndo />
      </MenuButton>
      <MenuButton onClick={() => editor.chain().focus().redo().run()}>
        <IoMdRedo />
      </MenuButton>
    </>
  );
};

export const Tiptap = ({
  defaultValue,
  fieldError,
  name,
  label,
}: {
  name: string;
  label: string;
  fieldError?: string;
  defaultValue?: string;
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Iframe,
      LinkExtension.configure({ openOnClick: false }),
    ],
    content: defaultValue,
    onUpdate: ({ editor }) => {
      setHtmlContent(editor.getHTML());
    },
  });
  const [htmlContent, setHtmlContent] = useState(defaultValue);

  return (
    <>
      <label className="text-sm font-bold" data-forminput>
        {label}:{" "}
      </label>
      <div className="bg-white my-4 border-2 focus-within:border-gray-800 border-gray-400 rounded-lg">
        <div className="p-1 rounded-sm border-b-2 border-gray-200">
          <MenuBar editor={editor} />
        </div>
        <div className="prose prose-sm">
          <EditorContent editor={editor} className="px-5" />
          <textarea
            name={name}
            value={htmlContent}
            readOnly
            className="hidden"
          />
        </div>

        {fieldError ? (
          <p className="text-sm text-red-500" role="alert" id={`${name}-error`}>
            {fieldError}
          </p>
        ) : null}
      </div>
    </>
  );
};
